import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle, Clock, Wrench,
  Plus, Eye, MapPin, RefreshCw, ArrowRight, CalendarDays, Users,
  Paperclip, X, FileText, Film, Image as ImageIcon, Upload
} from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { label: 'CRITIC', color: 'bg-red-500 text-white' },
  urgent: { label: 'URGENT', color: 'bg-orange-500 text-white' },
  high: { label: 'RIDICAT', color: 'bg-amber-500 text-white' },
  medium: { label: 'MEDIU', color: 'bg-yellow-500 text-slate-900' },
  low: { label: 'SCAZUT', color: 'bg-green-500 text-white' }
};

const STATUS_CONFIG = {
  open: { label: 'Nou', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Acceptat', color: 'bg-yellow-100 text-yellow-700' },
  in_progress: { label: 'In Lucru', color: 'bg-purple-100 text-purple-700' },
  waiting_parts: { label: 'Piese', color: 'bg-orange-100 text-orange-700' },
  resolved: { label: 'Rezolvat', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Inchis', color: 'bg-slate-100 text-slate-700' }
};

const ACTIVE_STATUSES = ['open', 'accepted', 'in_progress', 'waiting_parts'];

const FILE_TYPE_ICON = {
  image: ImageIcon,
  video: Film,
  document: FileText,
};

function getFileCategory(file) {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'document';
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const SectionPortalPage = () => {
  const { user } = useAuth();
  const { plants, locations, equipment, fetchEquipment } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [onDutyTechs, setOnDutyTechs] = useState({ on_duty: [], all_today: [], current_time: '' });

  // Derive user's accessible plants/locations
  const userPlants = user?.plant_access?.length > 0
    ? plants.filter(p => user.plant_access.includes(p.id))
    : plants;
  const userLocations = user?.location_access?.length > 0
    ? locations.filter(l => user.location_access.includes(l.id))
    : locations;

  const defaultPlantId = userPlants.length === 1 ? userPlants[0].id : '';
  const defaultLocationId = userLocations.length === 1 ? userLocations[0].id : '';

  const [newTicket, setNewTicket] = useState({
    title: '', description: '', equipment_id: '',
    priority_requested: 'medium',
    plant_id: defaultPlantId, location_id: defaultLocationId,
    reported_by_name: user?.full_name || '',
    reported_by_phone: user?.phone || ''
  });

  useEffect(() => {
    setNewTicket(prev => ({
      ...prev,
      plant_id: prev.plant_id || defaultPlantId,
      location_id: prev.location_id || defaultLocationId,
    }));
  }, [defaultPlantId, defaultLocationId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/tickets');
      let data = res.data || [];

      // Filter by access
      if (user?.plant_access?.length > 0) {
        data = data.filter(t => user.plant_access.includes(t.plant_id));
      }
      if (user?.location_access?.length > 0) {
        data = data.filter(t => user.location_access.includes(t.location_id));
      }

      setTickets(data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchOnDuty = useCallback(async () => {
    try {
      const res = await api.get('/shifts/on-duty');
      setOnDutyTechs(res.data || { on_duty: [], all_today: [], current_time: '' });
    } catch {
      // Silently fail - shifts may not be configured
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchOnDuty();
    const interval = setInterval(fetchData, 30000);
    const onDutyInterval = setInterval(fetchOnDuty, 60000);
    return () => { clearInterval(interval); clearInterval(onDutyInterval); };
  }, [fetchData, fetchOnDuty]);

  // Load all equipment on mount so dropdown is populated
  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 50 * 1024 * 1024; // 50MB
    const validFiles = files.filter(f => {
      if (f.size > maxSize) {
        toast.error(`${f.name} depaseste limita de 50MB`);
        return false;
      }
      return true;
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      // Create ticket
      const ticketPayload = {
        ...newTicket,
        source: 'section_portal',
        reporter_name: newTicket.reported_by_name || undefined,
        reporter_phone: newTicket.reported_by_phone || undefined,
        contact_info: newTicket.reported_by_phone ? {
          name: newTicket.reported_by_name, phone: newTicket.reported_by_phone
        } : null
      };
      const res = await api.post('/tickets', ticketPayload);
      const ticketId = res.data.id;

      // Upload attachments
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await api.post(`/tickets/${ticketId}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (err) {
            toast.error(`Eroare la incarcarea: ${file.name}`);
          }
        }
      }

      toast.success('Tichet creat cu succes!');
      setShowNewTicketDialog(false);
      setSelectedFiles([]);
      setNewTicket({
        title: '', description: '', equipment_id: '',
        priority_requested: 'medium',
        plant_id: defaultPlantId, location_id: defaultLocationId,
        reported_by_name: user?.full_name || '',
        reported_by_phone: user?.phone || ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la creare tichet');
    } finally {
      setUploading(false);
    }
  };

  // Stats
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const activeTickets = tickets.filter(t => ACTIVE_STATUSES.includes(t.status));
  const criticalTickets = activeTickets.filter(t => (t.priority_assigned || t.priority_requested) === 'critical');
  const overdueTickets = activeTickets.filter(t => (now - new Date(t.created_at)) > 48 * 3600000);
  const resolvedToday = tickets.filter(t => ['resolved', 'closed'].includes(t.status) && new Date(t.updated_at) >= today);
  const createdToday = tickets.filter(t => new Date(t.created_at) >= today);

  // Top 8 recent active
  const recentActive = activeTickets
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  // Technician workload
  const techCounts = {};
  activeTickets.forEach(t => {
    if (t.assigned_to_name) {
      techCounts[t.assigned_to_name] = (techCounts[t.assigned_to_name] || 0) + 1;
    }
  });
  const techWorkload = Object.entries(techCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const filteredLocations = userLocations.filter(l => !newTicket.plant_id || l.plant_id === newTicket.plant_id);
  // Show equipment matching location, OR equipment with no location assigned (global/unassigned)
  const filteredEquipment = equipment.filter(e =>
    !newTicket.location_id || !e.location_id || e.location_id === newTicket.location_id
  );

  const formatRelative = (d) => {
    if (!d) return '';
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}z`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-20 lg:pb-6" data-testid="section-portal-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard Sectie</h1>
          <p className="text-sm text-slate-500">
            {userPlants.length === 1 && <span className="font-medium text-blue-600">{userPlants[0].name}</span>}
            {userLocations.length === 1 && <span className="font-medium text-blue-600"> / {userLocations[0].name}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Reimprospateaza
          </Button>
          <Button onClick={() => setShowNewTicketDialog(true)} className="bg-blue-600 hover:bg-blue-700" size="sm" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Raporteaza Problema
          </Button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="kpi-cards">
        <Card className={`${activeTickets.length > 0 ? 'border-red-200 bg-gradient-to-br from-red-50 to-white' : ''}`} data-testid="kpi-active">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-red-600">{activeTickets.length}</p>
                <p className="text-xs text-slate-500 font-medium">Active</p>
              </div>
              <AlertTriangle className="w-7 h-7 text-red-300" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${criticalTickets.length > 0 ? 'border-red-300 bg-gradient-to-br from-red-100 to-white' : ''}`} data-testid="kpi-critical">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-red-700">{criticalTickets.length}</p>
                <p className="text-xs text-slate-500 font-medium">Critice</p>
              </div>
              <AlertTriangle className="w-7 h-7 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${overdueTickets.length > 0 ? 'border-orange-200 bg-gradient-to-br from-orange-50 to-white' : ''}`} data-testid="kpi-overdue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-600">{overdueTickets.length}</p>
                <p className="text-xs text-slate-500 font-medium">Intarziate</p>
              </div>
              <Clock className="w-7 h-7 text-orange-300" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-created-today">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{createdToday.length}</p>
                <p className="text-xs text-slate-500 font-medium">Noi azi</p>
              </div>
              <CalendarDays className="w-7 h-7 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-resolved-today">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-600">{resolvedToday.length}</p>
                <p className="text-xs text-slate-500 font-medium">Rezolvate azi</p>
              </div>
              <CheckCircle className="w-7 h-7 text-green-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-sm text-slate-500">Se incarca...</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Active Tickets - Main Area */}
          <div className="lg:col-span-2">
            <Card data-testid="active-tickets-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-base">Tichete Active</CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">{activeTickets.length} necesita atentie</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/portal/tickets')} data-testid="view-all-tickets-btn">
                  Vezi toate <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                {recentActive.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
                    <p className="text-sm text-slate-500">Niciun tichet activ</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {recentActive.map(ticket => {
                      const prio = ticket.priority_assigned || ticket.priority_requested || 'medium';
                      const pCfg = PRIORITY_CONFIG[prio] || PRIORITY_CONFIG.medium;
                      const sCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                      const isOld = (now - new Date(ticket.created_at)) > 48 * 3600000;

                      return (
                        <div
                          key={ticket.id}
                          data-testid={`dashboard-ticket-${ticket.id}`}
                          onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
                          className={`flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors cursor-pointer ${isOld ? 'border-l-4 border-l-red-400' : 'border-slate-100'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Badge className={`text-[10px] px-1.5 py-0 h-5 ${pCfg.color}`}>{pCfg.label}</Badge>
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 ${sCfg.color}`}>{sCfg.label}</Badge>
                            </div>
                            <p className="text-sm font-medium text-slate-900 truncate">{ticket.title}</p>
                            <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                              <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{ticket.location_name || '-'}</span>
                              {ticket.equipment_name && <span className="flex items-center gap-0.5"><Wrench className="w-3 h-3" />{ticket.equipment_name}</span>}
                              {ticket.assigned_to_name && <span>&rarr; {ticket.assigned_to_name}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatRelative(ticket.created_at)}</span>
                            <Eye className="w-4 h-4 text-slate-300" />
                          </div>
                        </div>
                      );
                    })}
                    {activeTickets.length > 8 && (
                      <p className="text-center text-xs text-slate-400 pt-2">
                        + {activeTickets.length - 8} alte tichete active
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            {/* Status breakdown */}
            <Card data-testid="status-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Distributie Status</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                    const count = tickets.filter(t => t.status === key).length;
                    if (count === 0) return null;
                    const pct = tickets.length > 0 ? Math.round((count / tickets.length) * 100) : 0;
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <Badge className={`text-[10px] px-2 py-0.5 min-w-[70px] justify-center ${cfg.color}`}>{cfg.label}</Badge>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t flex items-center justify-between">
                  <span className="text-xs text-slate-500">Total tichete</span>
                  <span className="text-sm font-bold text-slate-700">{tickets.length}</span>
                </div>
              </CardContent>
            </Card>

            {/* Technician Workload */}
            <Card data-testid="tech-workload-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-slate-400" /> Sarcini Tehnicieni
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {techWorkload.length === 0 ? (
                  <p className="text-xs text-slate-500 py-3">Niciun tichet asignat</p>
                ) : (
                  <div className="space-y-2">
                    {techWorkload.map((tech, i) => (
                      <div key={tech.name} className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          i === 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        }`}>{i + 1}</div>
                        <span className="text-xs text-slate-700 flex-1 truncate">{tech.name}</span>
                        <Badge variant="outline" className="text-[10px]">{tech.count} active</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* On-Duty Technicians by Shift */}
            <Card data-testid="on-duty-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Wrench className="w-4 h-4 text-green-500" /> Tehnicieni pe Schimb
                </CardTitle>
                {onDutyTechs.current_time && (
                  <p className="text-[10px] text-slate-400">Ora curentă: {onDutyTechs.current_time}</p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {onDutyTechs.all_today.length === 0 ? (
                  <p className="text-xs text-slate-500 py-3">Niciun schimb configurat pentru azi</p>
                ) : (
                  <div className="space-y-2">
                    {onDutyTechs.all_today.map((tech, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tech.is_on_duty ? 'bg-green-500' : 'bg-slate-300'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{tech.technician_name}</p>
                          <p className="text-[10px] text-slate-400">{tech.shift_name} · {tech.start_time}–{tech.end_time}</p>
                        </div>
                        {tech.is_on_duty && (
                          <Badge className="text-[10px] bg-green-100 text-green-700 border-0 px-1.5">Activ</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicketDialog} onOpenChange={(open) => { setShowNewTicketDialog(open); if (!open) setSelectedFiles([]); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Raporteaza Problema</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            {/* Operator Info - Auto-filled from profile */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs font-medium text-blue-700 mb-2">Informatii Raportare</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-blue-600">Nume</Label>
                  <Input value={newTicket.reported_by_name} onChange={(e) => setNewTicket({ ...newTicket, reported_by_name: e.target.value })} placeholder="Nume complet" className="bg-white h-9 text-sm" data-testid="operator-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-blue-600">Telefon</Label>
                  <Input value={newTicket.reported_by_phone} onChange={(e) => setNewTicket({ ...newTicket, reported_by_phone: e.target.value })} placeholder="Telefon" className="bg-white h-9 text-sm" data-testid="operator-phone-input" />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Titlu Problema *</Label>
              <Input value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} required placeholder="Descriere scurta a problemei" className="h-10" data-testid="ticket-title-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Fabrica *</Label>
                <Select value={newTicket.plant_id} onValueChange={(v) => setNewTicket({ ...newTicket, plant_id: v, location_id: '', equipment_id: '' })}>
                  <SelectTrigger className="h-10" data-testid="plant-select">
                    <SelectValue placeholder="Selecteaza" />
                  </SelectTrigger>
                  <SelectContent>
                    {userPlants.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Locatie *</Label>
                <Select value={newTicket.location_id} onValueChange={(v) => setNewTicket({ ...newTicket, location_id: v, equipment_id: '' })} disabled={!newTicket.plant_id}>
                  <SelectTrigger className="h-10" data-testid="location-select">
                    <SelectValue placeholder="Selecteaza" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Echipament</Label>
                <Select value={newTicket.equipment_id} onValueChange={(v) => setNewTicket({ ...newTicket, equipment_id: v })} disabled={!newTicket.location_id}>
                  <SelectTrigger className="h-10" data-testid="equipment-select">
                    <SelectValue placeholder="Selecteaza" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredEquipment.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Prioritate *</Label>
                <Select value={newTicket.priority_requested} onValueChange={(v) => setNewTicket({ ...newTicket, priority_requested: v })}>
                  <SelectTrigger className="h-10" data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critic</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">Ridicat</SelectItem>
                    <SelectItem value="medium">Mediu</SelectItem>
                    <SelectItem value="low">Scazut</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Descriere *</Label>
              <Textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} required rows={3} placeholder="Descrieti problema..." data-testid="ticket-description-input" />
            </div>

            {/* File Attachments */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Atasamente (optional)
              </Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-blue-300 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload-input"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Click pentru a selecta fisiere</p>
                  <p className="text-[11px] text-slate-400 mt-1">Poze, Video, PDF, Word, Excel (max 50MB/fisier)</p>
                </label>
              </div>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <div className="space-y-1.5" data-testid="selected-files-list">
                  {selectedFiles.map((file, idx) => {
                    const category = getFileCategory(file);
                    const Icon = FILE_TYPE_ICON[category] || FileText;
                    return (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`selected-file-${idx}`}>
                        <Icon className={`w-4 h-4 flex-shrink-0 ${category === 'image' ? 'text-green-500' : category === 'video' ? 'text-purple-500' : 'text-blue-500'}`} />
                        <span className="text-xs text-slate-700 flex-1 truncate">{file.name}</span>
                        <span className="text-[10px] text-slate-400">{formatFileSize(file.size)}</span>
                        <button type="button" onClick={() => removeFile(idx)} className="p-0.5 hover:bg-red-50 rounded" data-testid={`remove-file-${idx}`}>
                          <X className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowNewTicketDialog(false); setSelectedFiles([]); }}>Anuleaza</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={uploading} data-testid="submit-ticket-btn">
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Se creeaza...
                  </span>
                ) : 'Creeaza Tichet'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionPortalPage;
